/**
 * Quotations — Cockpit-Tab fuer ERPNext-Angebote.
 *
 * Tabelle mit aufklappbaren Zeilen analog zu Services:
 *   - Liste aller submitted Quotations fuer den Org-Owner
 *   - PDF-Anzeige im Modal (Print-Format Angebot_V5)
 *   - "Annehmen"-Button (Owner-only) → ERPNext convert_quotation_to_sales_order
 *
 * Quotations sind nur fuer Dienstleistungen / Sonderauftraege.
 * Subscriptions laufen separat ueber den Service-Katalog (Stripe).
 */

import { isTenantInstance } from '../lib/runtimeConfig'
import { useEffect, useState } from 'react'
import { useAuth } from '@it-sce/ui/contexts/KeycloakAuthContext'
import { useToast } from '../components/ui'
import PdfViewerModal from '../components/PdfViewerModal'
import { isActionable, quotationStatus, reasonValid, REASON_MAX } from '../lib/quotationStatus'

interface Organization {
  id: number
  name: string
  slug: string
  public_token?: string
  member_role: string
}

interface Quotation {
  name: string
  transaction_date: string
  valid_till: string | null
  grand_total: number
  currency: string
  status: string
  order_type?: string
}

interface Props {
  organization: Organization
}

const fmtDate = (s?: string | null) =>
  s ? new Date(s).toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' }) : '–'

const fmtAmount = (n: number, c: string) => `${n.toFixed(2)} ${c}`

export default function Quotations({ organization }: Props) {
  const { api: keycloakApi } = useAuth()
  const { showToast } = useToast()
  const isOwner = organization.member_role === 'owner'

  const [quotations, setQuotations] = useState<Quotation[]>([])
  const [loading, setLoading] = useState(true)
  const [accepting, setAccepting] = useState<string | null>(null)
  const [confirm, setConfirm] = useState<Quotation | null>(null)
  const [declining, setDeclining] = useState<Quotation | null>(null)
  const [declineReason, setDeclineReason] = useState('')
  const [declineBusy, setDeclineBusy] = useState(false)
  const [viewerId, setViewerId] = useState<string | null>(null)

  useEffect(() => {
    let cancelled = false
    async function load() {
      setLoading(true)
      try {
        const r = await keycloakApi!.get(`/api/org/${organization.public_token || organization.slug}/quotations`)
        if (cancelled) return
        setQuotations(r.data?.quotations ?? [])
      } catch (e) {
        const err = e as { response?: { data?: { error?: string } } }
        showToast('error', `Angebote konnten nicht geladen werden: ${err.response?.data?.error ?? 'Unbekannt'}`)
      } finally {
        if (!cancelled) setLoading(false)
      }
    }
    void load()
    return () => { cancelled = true }
  }, [organization.public_token, organization.slug, keycloakApi, showToast])

  async function loadPdf(id: string): Promise<Blob> {
    const res = await keycloakApi!.get(
      `/api/org/${organization.public_token || organization.slug}/quotations/pdf`,
      { responseType: 'blob', params: { id } }
    )
    return res.data as Blob
  }

  async function doAccept(q: Quotation) {
    setAccepting(q.name)
    try {
      const r = await keycloakApi!.post(
        `/api/org/${organization.public_token || organization.slug}/quotations/accept`,
        { confirm: true }, { params: { id: q.name } }
      )
      const so = r.data?.sales_order
      showToast('success', so
        ? `Angebot ${q.name} angenommen — Auftrag ${so} erstellt.`
        : (r.data?.message ?? `Angebot ${q.name} angenommen.`))
      // Liste neu laden — Status ist jetzt 'Ordered'
      const list = await keycloakApi!.get(`/api/org/${organization.public_token || organization.slug}/quotations`)
      setQuotations(list.data?.quotations ?? [])
    } catch (e) {
      const err = e as { response?: { data?: { error?: string } } }
      showToast('error', `Annahme fehlgeschlagen: ${err.response?.data?.error ?? 'Unbekannt'}`)
    } finally {
      setAccepting(null)
      setConfirm(null)
    }
  }

  async function doDecline(q: Quotation) {
    if (!reasonValid(declineReason)) return
    setDeclineBusy(true)
    try {
      await keycloakApi!.post(
        `/api/org/${organization.public_token || organization.slug}/quotations/decline`,
        { confirm: true, reason: declineReason.trim() }, { params: { id: q.name } }
      )
      showToast('success', `Angebot ${q.name} abgelehnt.`)
      const list = await keycloakApi!.get(`/api/org/${organization.public_token || organization.slug}/quotations`)
      setQuotations(list.data?.quotations ?? [])
      setDeclining(null)
      setDeclineReason('')
    } catch (e) {
      const err = e as { response?: { data?: { error?: string } } }
      showToast('error', `Ablehnung fehlgeschlagen: ${err.response?.data?.error ?? 'Unbekannt'}`)
    } finally {
      setDeclineBusy(false)
    }
  }

  // Annehmen/Ablehnen nur im Status Open, nicht nach valid_till (TQ #960), und nur fuer Inhaber. Das Ablehnen gibt es
  // nur in der Cockpit-Instanz (das geteilte Backend kennt die Route nicht).
  const isAcceptable = (q: Quotation) => isActionable(q.status, q.valid_till)
  const canDecline = isTenantInstance()

  return (
    <div>
      <p style={{ margin: '0 0 1rem', color: '#6b7280', fontSize: '0.875rem' }}>
        {loading
          ? 'Lade …'
          : quotations.length === 0
            ? 'Keine offenen Angebote'
            : `${quotations.length} Angebot${quotations.length === 1 ? '' : 'e'}`}
      </p>

      {!loading && quotations.length === 0 ? (
        <div style={{ padding: '1.5rem', background: '#f9fafb', borderRadius: 8, color: '#6b7280', textAlign: 'center' }}>
          Aktuell liegen keine Angebote für {organization.name} vor.
          <div style={{ fontSize: '0.8125rem', marginTop: '0.5rem' }}>
            Sobald wir Ihnen ein Angebot ausstellen, erscheint es hier zur Ansicht und Annahme.
          </div>
        </div>
      ) : (
        <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 8, overflow: 'hidden' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.875rem' }}>
            <thead>
              <tr style={{ background: '#f9fafb', borderBottom: '1px solid #e5e7eb', textAlign: 'left' }}>
                <th style={{ padding: '0.75rem 1rem' }}>Angebot</th>
                <th style={{ padding: '0.75rem 0.5rem' }}>Datum</th>
                <th style={{ padding: '0.75rem 0.5rem' }}>Gültig bis</th>
                <th style={{ padding: '0.75rem 0.5rem' }}>Status</th>
                <th style={{ padding: '0.75rem 0.5rem', textAlign: 'right' }}>Betrag</th>
                <th style={{ padding: '0.75rem 1rem', textAlign: 'right' }}>Aktion</th>
              </tr>
            </thead>
            <tbody>
              {quotations.map((q) => {
                const accept = isAcceptable(q)
                const st = quotationStatus(q.status, q.valid_till)
                return (
                  <tr key={q.name} style={{ borderBottom: '1px solid #f3f4f6' }}>
                    <td style={{ padding: '0.75rem 1rem' }}>
                      <code style={{ fontSize: '0.8125rem' }}>{q.name}</code>
                    </td>
                    <td style={{ padding: '0.75rem 0.5rem', color: '#6b7280' }}>
                      {fmtDate(q.transaction_date)}
                    </td>
                    <td style={{ padding: '0.75rem 0.5rem', color: q.valid_till ? '#1f2937' : '#9ca3af' }}>
                      {fmtDate(q.valid_till)}
                    </td>
                    <td style={{ padding: '0.75rem 0.5rem' }}>
                      <span style={{
                        padding: '2px 8px', borderRadius: 999, fontSize: '0.75rem', fontWeight: 600,
                        background: st.tone === 'accepted' ? '#d1fae5' : st.tone === 'declined' ? '#fee2e2' : '#fef3c7',
                        color: st.tone === 'accepted' ? '#065f46' : st.tone === 'declined' ? '#991b1b' : '#92400e',
                      }}>
                        {st.label}
                      </span>
                    </td>
                    <td style={{ padding: '0.75rem 0.5rem', textAlign: 'right',
                      fontVariantNumeric: 'tabular-nums', fontWeight: 600 }}>
                      {fmtAmount(q.grand_total, q.currency)}
                    </td>
                    <td style={{ padding: '0.75rem 1rem', textAlign: 'right' }}>
                      <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'flex-end' }}>
                        <button
                          type="button"
                          onClick={() => setViewerId(q.name)}
                          title="Angebot als PDF anzeigen"
                          style={{
                            padding: '4px 10px', border: '1px solid #d1d5db',
                            borderRadius: 4, background: '#fff', color: '#1f2937',
                            cursor: 'pointer', fontSize: '0.8125rem',
                          }}>
                          📄 Anzeigen
                        </button>
                        {accept && isOwner && (
                          <button
                            type="button"
                            onClick={() => setConfirm(q)}
                            disabled={accepting === q.name}
                            style={{
                              padding: '4px 10px', border: 'none',
                              borderRadius: 4, background: '#10b981', color: '#fff',
                              cursor: accepting === q.name ? 'wait' : 'pointer',
                              fontSize: '0.8125rem', fontWeight: 600,
                            }}>
                            ✓ Annehmen
                          </button>
                        )}
                        {accept && isOwner && canDecline && (
                          <button
                            type="button"
                            onClick={() => { setDeclineReason(''); setDeclining(q) }}
                            style={{
                              padding: '4px 10px', border: '1px solid #fca5a5',
                              borderRadius: 4, background: '#fff', color: '#b91c1c',
                              cursor: 'pointer', fontSize: '0.8125rem', fontWeight: 600,
                            }}>
                            ✕ Ablehnen
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* PDF-Viewer-Modal */}
      {viewerId && (
        <PdfViewerModal
          key={viewerId}
          title={`Angebot ${viewerId}`}
          filename={viewerId}
          load={() => loadPdf(viewerId)}
          onClose={() => setViewerId(null)}
        />
      )}

      {/* Annehmen-Confirm-Dialog */}
      {confirm && (
        <div onClick={() => setConfirm(null)}
          style={{
            position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            zIndex: 1001, padding: '2rem',
          }}>
          <div onClick={(e) => e.stopPropagation()}
            style={{ background: '#fff', borderRadius: 8, padding: '1.5rem', maxWidth: 480 }}>
            <h2 style={{ marginTop: 0 }}>Angebot annehmen?</h2>
            <p style={{ color: '#4b5563', lineHeight: 1.5 }}>
              Sie sind dabei, das Angebot <code>{confirm.name}</code> verbindlich anzunehmen.
              Wir erstellen dafür einen Auftrag in unserem System und setzen die
              Leistungen entsprechend um. Eine Rechnung oder Aufladung wird dadurch nicht ausgelöst.
            </p>
            <div style={{
              padding: '0.75rem 1rem', background: '#f9fafb', borderRadius: 6,
              fontSize: '0.875rem', margin: '1rem 0',
            }}>
              <div><strong>Betrag:</strong> {fmtAmount(confirm.grand_total, confirm.currency)}</div>
              {confirm.valid_till && (
                <div><strong>Gültig bis:</strong> {fmtDate(confirm.valid_till)}</div>
              )}
            </div>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.5rem' }}>
              <button type="button" onClick={() => setConfirm(null)} disabled={accepting !== null}
                style={{
                  padding: '0.625rem 1rem', border: '1px solid #d1d5db',
                  borderRadius: 6, background: '#fff', cursor: 'pointer', fontSize: '0.875rem',
                }}>Abbrechen</button>
              <button type="button" onClick={() => doAccept(confirm)} disabled={accepting !== null}
                style={{
                  padding: '0.625rem 1rem', border: 'none',
                  borderRadius: 6, background: '#10b981', color: '#fff',
                  cursor: accepting !== null ? 'wait' : 'pointer', fontSize: '0.875rem',
                  fontWeight: 600,
                }}>
                {accepting !== null ? 'Wird angenommen …' : '✓ Verbindlich annehmen'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Ablehnen-Dialog mit Grund */}
      {declining && (
        <div onClick={() => !declineBusy && setDeclining(null)}
          style={{
            position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            zIndex: 1001, padding: '2rem',
          }}>
          <div onClick={(e) => e.stopPropagation()}
            style={{ background: '#fff', borderRadius: 8, padding: '1.5rem', maxWidth: 480, width: '100%' }}>
            <h2 style={{ marginTop: 0 }}>Angebot ablehnen?</h2>
            <p style={{ color: '#4b5563', lineHeight: 1.5 }}>
              Sie lehnen das Angebot <code>{declining.name}</code> ab. Es wird als abgelehnt
              markiert. Bitte nennen Sie uns den Grund.
            </p>
            <label htmlFor="decline-reason" style={{ display: 'block', fontSize: '0.875rem', fontWeight: 600 }}>
              Grund der Ablehnung
            </label>
            <textarea
              id="decline-reason"
              value={declineReason}
              onChange={(e) => setDeclineReason(e.target.value)}
              maxLength={REASON_MAX}
              rows={4}
              style={{
                width: '100%', boxSizing: 'border-box', marginTop: 4, padding: '0.5rem',
                border: '1px solid #d1d5db', borderRadius: 6, fontSize: '0.875rem',
              }}
            />
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.5rem', marginTop: '1rem' }}>
              <button type="button" onClick={() => setDeclining(null)} disabled={declineBusy}
                style={{
                  padding: '0.625rem 1rem', border: '1px solid #d1d5db',
                  borderRadius: 6, background: '#fff', cursor: 'pointer', fontSize: '0.875rem',
                }}>Abbrechen</button>
              <button type="button" onClick={() => doDecline(declining)}
                disabled={declineBusy || !reasonValid(declineReason)}
                style={{
                  padding: '0.625rem 1rem', border: 'none', borderRadius: 6,
                  background: reasonValid(declineReason) ? '#dc2626' : '#fca5a5', color: '#fff',
                  cursor: declineBusy ? 'wait' : 'pointer', fontSize: '0.875rem', fontWeight: 600,
                }}>
                {declineBusy ? 'Wird abgelehnt …' : 'Verbindlich ablehnen'}
              </button>
            </div>
          </div>
        </div>
      )}

      {!isOwner && quotations.some((q) => isAcceptable(q)) && (
        <p style={{ marginTop: '1rem', color: '#6b7280', fontSize: '0.8125rem' }}>
          Nur Inhaber dürfen Angebote verbindlich annehmen.
        </p>
      )}
    </div>
  )
}
